/**
 * Pure shop-map view helpers — no Leaflet, no React.
 * G-RADIUS: default 10 / min 0.1 / max 500 / ≤2 dp / comma→dot / step 0.1.
 * EV-G8-SESSION / Exit G-SESSION: closed MapViewActiveV1 + MapViewWizardDraftV1;
 * session-only last-applied (ADR-MV-12) — reject DB last-applied.
 * ADR-MV-04: saved around_me FREEZE stored center; ephemeral uses live GPS.
 *
 * HOST-OWNED framing state lives in ShopsDirectoryMap; this module is SSOT for
 * radius parse, session active/draft shapes, and FREEZE vs live center resolve.
 */

import type { MapCustomViewDTO } from '../account/mapViewsTypes.js';
import { validLatLng } from './shopMapUtils.js';

/** Exact session key — Exit G-SESSION Pass (ban short alias alone). */
export const MAP_VIEW_ACTIVE_SESSION_KEY =
  'customer:directory:mapViewActive:v1' as const;

/** Exact draft key — ADR-MV-10 / Exit G-SESSION (ban short alias alone). */
export const MAP_VIEW_WIZARD_DRAFT_SESSION_KEY =
  'customer:directory:mapViewWizardDraft:v1' as const;

export const MAP_VIEW_RADIUS_KM_DEFAULT = 10;
export const MAP_VIEW_RADIUS_KM_MIN = 0.1;
export const MAP_VIEW_RADIUS_KM_MAX = 500;
/** G-RADIUS input step — Views Around-me editor `step="0.1"`. */
export const MAP_VIEW_RADIUS_KM_STEP = 0.1;
export const MAP_VIEW_RADIUS_KM_MAX_FRACTION_DIGITS = 2;

/** Camera framing mode — FitBoundsWhenReady shops∪user only when `'shops'`. */
export type ShopMapFramingMode = 'shops' | 'myLocation' | 'radius' | 'bounds';

/** Alias kept for plan TypeScript block (`mode` field). */
export type MapViewFramingMode = ShopMapFramingMode;

export type ShopMapBuiltinId = 'general' | 'myLocation' | 'aroundMe';

export type MapViewBuiltinId = ShopMapBuiltinId;

export type MapViewKind =
  | 'around_me'
  | 'around_place'
  | 'map_camera'
  | 'exact_bounds';

/**
 * Closed discriminant — Exit G-SESSION Pass.
 * Fail: soft optional `builtinId` on both arms.
 */
export type MapViewActiveV1 =
  | {
      viewId: null;
      builtinId: MapViewBuiltinId;
      mode: MapViewFramingMode;
      ephemeral?: {
        radiusKm: number;
        centerLat?: number;
        centerLng?: number;
      };
    }
  | {
      viewId: string;
      builtinId?: never;
      mode: MapViewFramingMode;
    };

export type MapViewWizardDraftV1 = {
  kind: MapViewKind;
  step?: 'configure' | 'name' | 'confirm';
  name?: string;
  radiusKm?: number;
  centerLat?: number;
  centerLng?: number;
  placeLabel?: string;
  swLat?: number;
  swLng?: number;
  neLat?: number;
  neLng?: number;
  createdAtMs: number;
};

export type ParseRadiusKmError =
  | 'invalid'
  | 'tooSmall'
  | 'tooLarge'
  | 'tooManyDecimals';

export type ParseRadiusKmResult =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly error: ParseRadiusKmError };

/** Normalize locale decimal comma, then validate G-RADIUS. */
export function parseRadiusKm(raw: string): ParseRadiusKmResult {
  const trimmed = raw.trim().replace(/,/g, '.');
  if (trimmed.length === 0) {
    return { ok: false, error: 'invalid' };
  }
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return { ok: false, error: 'invalid' };
  }
  const dot = trimmed.indexOf('.');
  if (
    dot >= 0 &&
    trimmed.length - dot - 1 > MAP_VIEW_RADIUS_KM_MAX_FRACTION_DIGITS
  ) {
    return { ok: false, error: 'tooManyDecimals' };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, error: 'invalid' };
  }
  if (value < MAP_VIEW_RADIUS_KM_MIN) {
    return { ok: false, error: 'tooSmall' };
  }
  if (value > MAP_VIEW_RADIUS_KM_MAX) {
    return { ok: false, error: 'tooLarge' };
  }
  return { ok: true, value };
}

/** @deprecated Prefer `parseRadiusKm` — alias for plan naming. */
export function parseRadiusKmInput(raw: string): ParseRadiusKmResult {
  return parseRadiusKm(raw);
}

/** Format radius for controlled inputs — ≤2 dp, trim trailing zeros. */
export function formatRadiusKm(value: number): string {
  if (!Number.isFinite(value)) {
    return String(MAP_VIEW_RADIUS_KM_DEFAULT);
  }
  const clamped = Math.min(
    MAP_VIEW_RADIUS_KM_MAX,
    Math.max(MAP_VIEW_RADIUS_KM_MIN, value),
  );
  const rounded =
    Math.round(clamped * 10 ** MAP_VIEW_RADIUS_KM_MAX_FRACTION_DIGITS) /
    10 ** MAP_VIEW_RADIUS_KM_MAX_FRACTION_DIGITS;
  return String(rounded);
}

/** 12.5 km → 12500 m (G-RADIUS unit row). */
export function radiusKmToMeters(radiusKm: number): number {
  return radiusKm * 1000;
}

/**
 * Saved view row for Views menu + FREEZE apply (ADR-MV-04).
 * Geometry fields mirror MapCustomViewDTO so host can frame without re-fetch.
 */
export type ShopMapSavedViewRow = {
  readonly id: number;
  readonly name: string;
  readonly kind: MapViewKind;
  readonly radiusKm: number | null;
  readonly centerLat: number | null;
  readonly centerLng: number | null;
  readonly placeLabel: string | null;
  readonly swLat: number | null;
  readonly swLng: number | null;
  readonly neLat: number | null;
  readonly neLng: number | null;
};

/** DTO → host row (type-only account import; shops UI stays off account chrome). */
export function shopMapSavedViewRowFromDto(
  dto: MapCustomViewDTO,
): ShopMapSavedViewRow {
  return {
    id: dto.id,
    name: dto.name,
    kind: dto.kind,
    radiusKm: dto.radiusKm,
    centerLat: dto.centerLat,
    centerLng: dto.centerLng,
    placeLabel: dto.placeLabel,
    swLat: dto.swLat,
    swLng: dto.swLng,
    neLat: dto.neLat,
    neLng: dto.neLng,
  };
}

/** Radius Circle overlay — `liveGps: true` tracks parent GPS; false FREEZES center. */
export type ShopMapRadiusOverlay = {
  readonly centerLat: number;
  readonly centerLng: number;
  readonly radiusKm: number;
  /** Ephemeral Around-me = true; saved around_me / around_place = false (ADR-MV-04). */
  readonly liveGps: boolean;
};

/** Exact bounds / map_camera Rectangle overlay. */
export type ShopMapBoundsOverlay = {
  readonly swLat: number;
  readonly swLng: number;
  readonly neLat: number;
  readonly neLng: number;
};

export type ShopMapOverlayKind = 'none' | 'circle' | 'rectangle';

/** Kind → framingMode apply table (plan freeze). */
export function framingModeForSavedKind(kind: MapViewKind): MapViewFramingMode {
  switch (kind) {
    case 'around_me':
    case 'around_place':
      return 'radius';
    case 'map_camera':
    case 'exact_bounds':
      return 'bounds';
  }
}

export function framingModeForBuiltin(
  builtinId: MapViewBuiltinId,
): MapViewFramingMode {
  switch (builtinId) {
    case 'general':
      return 'shops';
    case 'myLocation':
      return 'myLocation';
    case 'aroundMe':
      return 'radius';
  }
}

export function overlayKindForFramingMode(
  mode: MapViewFramingMode,
): ShopMapOverlayKind {
  switch (mode) {
    case 'radius':
      return 'circle';
    case 'bounds':
      return 'rectangle';
    case 'shops':
    case 'myLocation':
      return 'none';
  }
}

/**
 * ADR-MV-04 center resolution.
 * - `saved` / FREEZE: **stored** center only — never re-geolocate.
 * - `ephemeral`: live GPS center from the Apply gesture.
 */
export type AroundMeCenterSource =
  | {
      readonly kind: 'saved';
      readonly centerLat: number;
      readonly centerLng: number;
    }
  | {
      readonly kind: 'ephemeral';
      readonly liveLat: number;
      readonly liveLng: number;
    };

export type LatLngCenter = {
  readonly lat: number;
  readonly lng: number;
};

/**
 * Resolve Around-me Circle center.
 * Saved path ignores any live GPS the caller might also hold — FREEZE only.
 */
export function resolveAroundMeCenter(
  source: AroundMeCenterSource,
): LatLngCenter | null {
  if (source.kind === 'saved') {
    if (!validLatLng(source.centerLat, source.centerLng)) {
      return null;
    }
    return { lat: source.centerLat, lng: source.centerLng };
  }
  if (!validLatLng(source.liveLat, source.liveLng)) {
    return null;
  }
  return { lat: source.liveLat, lng: source.liveLng };
}

export type RadiusFramingState = {
  readonly mode: 'radius';
  readonly radiusKm: number;
  readonly centerLat: number;
  readonly centerLng: number;
  /** `frozen` = saved around_me (ADR-MV-04); `live` = ephemeral Around-me Apply. */
  readonly centerSource: 'frozen' | 'live';
};

/**
 * Saved `around_me` / `around_place` apply — FREEZE stored center+radiusKm.
 * Does not accept live GPS (callers must not re-geolocate).
 */
export function framingFromSavedAroundMe(args: {
  readonly centerLat: number;
  readonly centerLng: number;
  readonly radiusKm: number;
}): RadiusFramingState | null {
  const center = resolveAroundMeCenter({
    kind: 'saved',
    centerLat: args.centerLat,
    centerLng: args.centerLng,
  });
  if (center === null) {
    return null;
  }
  const parsed = parseRadiusKm(formatRadiusKm(args.radiusKm));
  if (!parsed.ok) {
    return null;
  }
  return {
    mode: 'radius',
    radiusKm: parsed.value,
    centerLat: center.lat,
    centerLng: center.lng,
    centerSource: 'frozen',
  };
}

/** Ephemeral Around-me Apply — live GPS center only. */
export function framingFromEphemeralAroundMe(args: {
  readonly radiusKm: number;
  readonly liveLat: number;
  readonly liveLng: number;
}): RadiusFramingState | null {
  const center = resolveAroundMeCenter({
    kind: 'ephemeral',
    liveLat: args.liveLat,
    liveLng: args.liveLng,
  });
  if (center === null) {
    return null;
  }
  const parsed = parseRadiusKm(formatRadiusKm(args.radiusKm));
  if (!parsed.ok) {
    return null;
  }
  return {
    mode: 'radius',
    radiusKm: parsed.value,
    centerLat: center.lat,
    centerLng: center.lng,
    centerSource: 'live',
  };
}

export function buildGeneralActive(): MapViewActiveV1 {
  return {
    viewId: null,
    builtinId: 'general',
    mode: 'shops',
  };
}

export function buildMyLocationActive(): MapViewActiveV1 {
  return {
    viewId: null,
    builtinId: 'myLocation',
    mode: 'myLocation',
  };
}

/** Ephemeral Around-me after Apply — session may stash live center for remount. */
export function buildEphemeralAroundMeActive(
  radiusKm: number,
  liveCenter: LatLngCenter | null,
): MapViewActiveV1 {
  const ephemeral: {
    radiusKm: number;
    centerLat?: number;
    centerLng?: number;
  } = { radiusKm };
  if (liveCenter !== null) {
    ephemeral.centerLat = liveCenter.lat;
    ephemeral.centerLng = liveCenter.lng;
  }
  return {
    viewId: null,
    builtinId: 'aroundMe',
    mode: 'radius',
    ephemeral,
  };
}

/** Saved custom view last-applied (geometry in DB list — session holds id+mode only). */
export function buildSavedViewActive(
  viewId: string,
  mode: MapViewFramingMode,
): MapViewActiveV1 {
  return {
    viewId,
    mode,
  };
}

/** P14a absent-key default — General / shops. */
export function defaultMapViewActive(): MapViewActiveV1 {
  return buildGeneralActive();
}

const FRAMING_MODES: ReadonlySet<string> = new Set([
  'shops',
  'myLocation',
  'radius',
  'bounds',
]);

const BUILTIN_IDS: ReadonlySet<string> = new Set([
  'general',
  'myLocation',
  'aroundMe',
]);

const MAP_VIEW_KINDS: ReadonlySet<string> = new Set([
  'around_me',
  'around_place',
  'map_camera',
  'exact_bounds',
]);

const WIZARD_STEPS: ReadonlySet<string> = new Set([
  'configure',
  'name',
  'confirm',
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFramingMode(value: unknown): value is MapViewFramingMode {
  return typeof value === 'string' && FRAMING_MODES.has(value);
}

function isBuiltinId(value: unknown): value is MapViewBuiltinId {
  return typeof value === 'string' && BUILTIN_IDS.has(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

type MapViewActiveEphemeral = {
  radiusKm: number;
  centerLat?: number;
  centerLng?: number;
};

function parseEphemeral(raw: unknown): MapViewActiveEphemeral | null {
  if (!isPlainObject(raw) || !isFiniteNumber(raw.radiusKm)) {
    return null;
  }
  const radiusParsed = parseRadiusKm(formatRadiusKm(raw.radiusKm));
  if (!radiusParsed.ok) {
    return null;
  }
  const out: MapViewActiveEphemeral = { radiusKm: radiusParsed.value };
  const hasLat = 'centerLat' in raw;
  const hasLng = 'centerLng' in raw;
  if (hasLat || hasLng) {
    if (!isFiniteNumber(raw.centerLat) || !isFiniteNumber(raw.centerLng)) {
      return null;
    }
    if (!validLatLng(raw.centerLat, raw.centerLng)) {
      return null;
    }
    out.centerLat = raw.centerLat;
    out.centerLng = raw.centerLng;
  }
  return out;
}

/**
 * Closed-discriminant parse. Soft optional builtinId on saved arm → null.
 * Corrupt / unknown → null (P14a: treat as absent).
 */
export function parseMapViewActiveV1(raw: unknown): MapViewActiveV1 | null {
  if (!isPlainObject(raw) || !isFramingMode(raw.mode)) {
    return null;
  }
  const mode = raw.mode;

  if (raw.viewId === null) {
    if (!isBuiltinId(raw.builtinId)) {
      return null;
    }
    const base: MapViewActiveV1 = {
      viewId: null,
      builtinId: raw.builtinId,
      mode,
    };
    if ('ephemeral' in raw && raw.ephemeral !== undefined) {
      const ephemeral = parseEphemeral(raw.ephemeral);
      if (ephemeral === null) {
        return null;
      }
      return { ...base, ephemeral };
    }
    return base;
  }

  if (typeof raw.viewId !== 'string' || raw.viewId.trim().length === 0) {
    return null;
  }
  // Closed discriminant: saved arm must not carry builtinId.
  if ('builtinId' in raw && raw.builtinId !== undefined) {
    return null;
  }
  return {
    viewId: raw.viewId,
    mode,
  };
}

export function parseMapViewWizardDraftV1(
  raw: unknown,
): MapViewWizardDraftV1 | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  if (typeof raw.kind !== 'string' || !MAP_VIEW_KINDS.has(raw.kind)) {
    return null;
  }
  if (!isFiniteNumber(raw.createdAtMs)) {
    return null;
  }
  const draft: MapViewWizardDraftV1 = {
    kind: raw.kind as MapViewKind,
    createdAtMs: raw.createdAtMs,
  };
  if ('step' in raw && raw.step !== undefined) {
    if (typeof raw.step !== 'string' || !WIZARD_STEPS.has(raw.step)) {
      return null;
    }
    draft.step = raw.step as MapViewWizardDraftV1['step'];
  }
  if ('name' in raw && raw.name !== undefined) {
    if (typeof raw.name !== 'string') {
      return null;
    }
    draft.name = raw.name;
  }
  if ('radiusKm' in raw && raw.radiusKm !== undefined) {
    if (!isFiniteNumber(raw.radiusKm)) {
      return null;
    }
    const parsed = parseRadiusKm(formatRadiusKm(raw.radiusKm));
    if (!parsed.ok) {
      return null;
    }
    draft.radiusKm = parsed.value;
  }
  if ('centerLat' in raw && raw.centerLat !== undefined) {
    if (!isFiniteNumber(raw.centerLat)) {
      return null;
    }
    draft.centerLat = raw.centerLat;
  }
  if ('centerLng' in raw && raw.centerLng !== undefined) {
    if (!isFiniteNumber(raw.centerLng)) {
      return null;
    }
    draft.centerLng = raw.centerLng;
  }
  if (
    draft.centerLat !== undefined &&
    draft.centerLng !== undefined &&
    !validLatLng(draft.centerLat, draft.centerLng)
  ) {
    return null;
  }
  if ('placeLabel' in raw && raw.placeLabel !== undefined) {
    if (typeof raw.placeLabel !== 'string') {
      return null;
    }
    draft.placeLabel = raw.placeLabel;
  }
  const boundKeys = ['swLat', 'swLng', 'neLat', 'neLng'] as const;
  for (const key of boundKeys) {
    if (key in raw && raw[key] !== undefined) {
      if (!isFiniteNumber(raw[key])) {
        return null;
      }
      draft[key] = raw[key];
    }
  }
  return draft;
}

export function serializeMapViewActiveV1(value: MapViewActiveV1): string {
  return JSON.stringify(value);
}

export function serializeMapViewWizardDraftV1(
  value: MapViewWizardDraftV1,
): string {
  return JSON.stringify(value);
}

function getSessionStorage(): Storage | null {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return null;
  }
  return window.sessionStorage;
}

function readSessionJson(key: string): unknown | null {
  const storage = getSessionStorage();
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(key);
    if (raw === null || raw.trim().length === 0) {
      return null;
    }
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, json: string): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(key, json);
  } catch {
    // private mode / quota — session last-applied best-effort
  }
}

function clearSessionKey(key: string): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Session-only last-applied (ADR-MV-12). Never invent DB last-applied. */
export function readMapViewActiveFromSession(): MapViewActiveV1 | null {
  return parseMapViewActiveV1(readSessionJson(MAP_VIEW_ACTIVE_SESSION_KEY));
}

export function writeMapViewActiveToSession(value: MapViewActiveV1): void {
  writeSessionJson(MAP_VIEW_ACTIVE_SESSION_KEY, serializeMapViewActiveV1(value));
}

export function clearMapViewActiveFromSession(): void {
  clearSessionKey(MAP_VIEW_ACTIVE_SESSION_KEY);
}

export function readMapViewWizardDraftFromSession(): MapViewWizardDraftV1 | null {
  return parseMapViewWizardDraftV1(
    readSessionJson(MAP_VIEW_WIZARD_DRAFT_SESSION_KEY),
  );
}

export function writeMapViewWizardDraftToSession(
  value: MapViewWizardDraftV1,
): void {
  writeSessionJson(
    MAP_VIEW_WIZARD_DRAFT_SESSION_KEY,
    serializeMapViewWizardDraftV1(value),
  );
}

export function clearMapViewWizardDraftFromSession(): void {
  clearSessionKey(MAP_VIEW_WIZARD_DRAFT_SESSION_KEY);
}

/**
 * G2 — thin guest CTA park payload (`buildGuestSignInMapViewDraft`).
 * Only kind (+ optional configure step) + createdAtMs; no geo/name progress.
 */
export function isThinGuestSignInMapViewDraft(
  draft: MapViewWizardDraftV1,
): boolean {
  if (draft.kind !== 'around_me') {
    return false;
  }
  if (draft.step !== undefined && draft.step !== 'configure') {
    return false;
  }
  return (
    draft.name === undefined &&
    draft.radiusKm === undefined &&
    draft.centerLat === undefined &&
    draft.centerLng === undefined &&
    draft.placeLabel === undefined &&
    draft.swLat === undefined &&
    draft.swLng === undefined &&
    draft.neLat === undefined &&
    draft.neLng === undefined
  );
}

/**
 * G2 — Guest “Sign in to save views” must not clobber a richer session SSOT draft.
 * Thin fallback → keep existing when present; non-thin park merges missing fields from session.
 */
export function resolveMapViewWizardDraftForSignInPark(
  fallback: MapViewWizardDraftV1,
): MapViewWizardDraftV1 {
  const existing = readMapViewWizardDraftFromSession();
  if (existing === null) {
    return fallback;
  }
  if (isThinGuestSignInMapViewDraft(fallback)) {
    return existing;
  }
  return {
    kind: fallback.kind,
    createdAtMs: fallback.createdAtMs,
    step: fallback.step ?? existing.step,
    name: fallback.name ?? existing.name,
    radiusKm: fallback.radiusKm ?? existing.radiusKm,
    centerLat: fallback.centerLat ?? existing.centerLat,
    centerLng: fallback.centerLng ?? existing.centerLng,
    placeLabel: fallback.placeLabel ?? existing.placeLabel,
    swLat: fallback.swLat ?? existing.swLat,
    swLng: fallback.swLng ?? existing.swLng,
    neLat: fallback.neLat ?? existing.neLat,
    neLng: fallback.neLng ?? existing.neLng,
  };
}

export type ResolveInitialMapViewActiveResult = {
  readonly active: MapViewActiveV1;
  /** P14a Pass: true only when a present, parseable session key restored framing. */
  readonly restoredFromSession: boolean;
};

/**
 * P14a remount helper.
 * - Present valid key → restore that framing.
 * - Absent / corrupt → default General/shops (`restoredFromSession: false`).
 */
export function resolveInitialMapViewActive(): ResolveInitialMapViewActiveResult {
  const fromSession = readMapViewActiveFromSession();
  if (fromSession === null) {
    return { active: defaultMapViewActive(), restoredFromSession: false };
  }
  return { active: fromSession, restoredFromSession: true };
}

/**
 * Derive radius overlay from restored builtin aroundMe ephemeral.
 * Remount uses the **session-stored** center (no geolocation re-acquire).
 * `centerSource` stays `'live'` to mark origin as ephemeral Apply (not a DB saved view).
 */
export function radiusFramingFromActiveEphemeral(
  active: MapViewActiveV1,
): RadiusFramingState | null {
  if (active.viewId !== null) {
    return null;
  }
  if (active.builtinId !== 'aroundMe' || active.mode !== 'radius') {
    return null;
  }
  const ephemeral = active.ephemeral;
  if (
    ephemeral === undefined ||
    ephemeral.centerLat === undefined ||
    ephemeral.centerLng === undefined
  ) {
    return null;
  }
  if (!validLatLng(ephemeral.centerLat, ephemeral.centerLng)) {
    return null;
  }
  const parsed = parseRadiusKm(formatRadiusKm(ephemeral.radiusKm));
  if (!parsed.ok) {
    return null;
  }
  return {
    mode: 'radius',
    radiusKm: parsed.value,
    centerLat: ephemeral.centerLat,
    centerLng: ephemeral.centerLng,
    centerSource: 'live',
  };
}

/** Convert FREEZE/live framing state into host Circle overlay (ADR-MV-04). */
export function radiusOverlayFromFraming(
  framing: RadiusFramingState,
): ShopMapRadiusOverlay {
  return {
    centerLat: framing.centerLat,
    centerLng: framing.centerLng,
    radiusKm: framing.radiusKm,
    liveGps: framing.centerSource === 'live',
  };
}

/** Host framing snapshot for P14a session remount (ShopsDirectoryMap). */
export type MapViewHostFramingRestore = {
  readonly framingMode: ShopMapFramingMode;
  readonly checkedBuiltin: ShopMapBuiltinId | null;
  readonly activeViewId: number | null;
  readonly ephemeralRadiusKm: number | null;
  readonly radiusCenter: LatLngCenter | null;
  /** false = FREEZE radiusCenter (saved / remount); true = track live GPS on Apply. */
  readonly radiusLiveGps: boolean;
  readonly bumpFitEpoch: boolean;
  readonly bumpRadiusFitEpoch: boolean;
  readonly bumpBoundsFitEpoch: boolean;
  readonly panToken: number;
};

function defaultHostFramingRestore(): MapViewHostFramingRestore {
  return {
    framingMode: 'shops',
    checkedBuiltin: 'general',
    activeViewId: null,
    ephemeralRadiusKm: null,
    radiusCenter: null,
    radiusLiveGps: false,
    bumpFitEpoch: false,
    bumpRadiusFitEpoch: false,
    bumpBoundsFitEpoch: false,
    panToken: 0,
  };
}

/**
 * Map session active → host state (P14a). Never calls geolocation.
 * Saved around_me / remount ephemeral uses FREEZE center from session.
 */
export function restoreHostFramingFromMapViewActive(
  active: MapViewActiveV1,
): MapViewHostFramingRestore {
  if (active.viewId !== null) {
    const parsedId = Number(active.viewId);
    const activeViewId =
      Number.isFinite(parsedId) && parsedId > 0 ? Math.trunc(parsedId) : null;
    return {
      framingMode: active.mode,
      checkedBuiltin: null,
      activeViewId,
      ephemeralRadiusKm: null,
      radiusCenter: null,
      radiusLiveGps: false,
      bumpFitEpoch: active.mode === 'shops',
      bumpRadiusFitEpoch: active.mode === 'radius',
      bumpBoundsFitEpoch: active.mode === 'bounds',
      panToken: 0,
    };
  }

  switch (active.builtinId) {
    case 'general':
      return {
        ...defaultHostFramingRestore(),
        bumpFitEpoch: true,
      };
    case 'myLocation':
      return {
        framingMode: 'myLocation',
        checkedBuiltin: 'myLocation',
        activeViewId: null,
        ephemeralRadiusKm: null,
        radiusCenter: null,
        radiusLiveGps: false,
        bumpFitEpoch: false,
        bumpRadiusFitEpoch: false,
        bumpBoundsFitEpoch: false,
        panToken: 0,
      };
    case 'aroundMe': {
      const frozen = radiusFramingFromActiveEphemeral(active);
      if (frozen !== null) {
        return {
          framingMode: 'radius',
          checkedBuiltin: 'aroundMe',
          activeViewId: null,
          ephemeralRadiusKm: frozen.radiusKm,
          radiusCenter: {
            lat: frozen.centerLat,
            lng: frozen.centerLng,
          },
          radiusLiveGps: false,
          bumpFitEpoch: false,
          bumpRadiusFitEpoch: true,
          bumpBoundsFitEpoch: false,
          panToken: 0,
        };
      }
      const km =
        active.ephemeral !== undefined &&
        Number.isFinite(active.ephemeral.radiusKm)
          ? active.ephemeral.radiusKm
          : null;
      return {
        framingMode: 'radius',
        checkedBuiltin: 'aroundMe',
        activeViewId: null,
        ephemeralRadiusKm: km,
        radiusCenter: null,
        radiusLiveGps: true,
        bumpFitEpoch: false,
        bumpRadiusFitEpoch: km !== null,
        bumpBoundsFitEpoch: false,
        panToken: 0,
      };
    }
    default:
      return defaultHostFramingRestore();
  }
}

export const MAP_VIEW_GEO_MAX_DECIMALS = 6;

/** Round WGS84 to ≤6 dp (map_camera / exact_bounds persist). */
export function roundGeo6dp(value: number): number {
  const factor = 10 ** MAP_VIEW_GEO_MAX_DECIMALS;
  return Math.round(value * factor) / factor;
}

/**
 * Parse one coordinate field — blank/comma/excess dp → null.
 * Caller validates lat/lng domain.
 */
export function parseGeoCoordinate6dp(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, '.');
  if (trimmed.length === 0 || !/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }
  const dot = trimmed.indexOf('.');
  if (dot >= 0 && trimmed.length - dot - 1 > MAP_VIEW_GEO_MAX_DECIMALS) {
    return null;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

export type ParseMapViewBoundsError =
  | 'invalid'
  | 'swGtNe'
  | 'antimeridian';

export type ParseMapViewBoundsResult =
  | {
      readonly ok: true;
      readonly bounds: ShopMapBoundsOverlay;
    }
  | { readonly ok: false; readonly error: ParseMapViewBoundsError };

/**
 * exact_bounds / map_camera SW–NE validation — reject antimeridian and SW>NE.
 */
export function parseMapViewBounds(input: {
  readonly swLat: string;
  readonly swLng: string;
  readonly neLat: string;
  readonly neLng: string;
}): ParseMapViewBoundsResult {
  const swLat = parseGeoCoordinate6dp(input.swLat);
  const swLng = parseGeoCoordinate6dp(input.swLng);
  const neLat = parseGeoCoordinate6dp(input.neLat);
  const neLng = parseGeoCoordinate6dp(input.neLng);
  if (swLat === null || swLng === null || neLat === null || neLng === null) {
    return { ok: false, error: 'invalid' };
  }
  if (
    swLat < -90 ||
    swLat > 90 ||
    neLat < -90 ||
    neLat > 90 ||
    swLng < -180 ||
    swLng > 180 ||
    neLng < -180 ||
    neLng > 180
  ) {
    return { ok: false, error: 'invalid' };
  }
  if (swLat > neLat) {
    return { ok: false, error: 'swGtNe' };
  }
  if (swLng > neLng) {
    return { ok: false, error: 'antimeridian' };
  }
  return {
    ok: true,
    bounds: {
      swLat: roundGeo6dp(swLat),
      swLng: roundGeo6dp(swLng),
      neLat: roundGeo6dp(neLat),
      neLng: roundGeo6dp(neLng),
    },
  };
}

export function normalizeMapViewBounds(
  bounds: ShopMapBoundsOverlay,
): ShopMapBoundsOverlay {
  return {
    swLat: roundGeo6dp(bounds.swLat),
    swLng: roundGeo6dp(bounds.swLng),
    neLat: roundGeo6dp(bounds.neLat),
    neLng: roundGeo6dp(bounds.neLng),
  };
}

/**
 * Pure FREEZE apply snapshot from a saved row (ADR-MV-04).
 * Radius path uses stored center only — never live GPS.
 */
export type SavedViewFramingApply =
  | {
      readonly framingMode: 'radius';
      readonly radiusKm: number;
      readonly centerLat: number;
      readonly centerLng: number;
      readonly radiusLiveGps: false;
      readonly boundsOverlay: null;
    }
  | {
      readonly framingMode: 'bounds';
      readonly boundsOverlay: ShopMapBoundsOverlay;
      readonly ephemeralRadiusKm: null;
      readonly radiusCenter: null;
      readonly radiusLiveGps: false;
    };

export function framingApplyFromSavedViewRow(
  row: ShopMapSavedViewRow,
): SavedViewFramingApply | null {
  if (row.kind === 'around_me' || row.kind === 'around_place') {
    if (
      row.centerLat === null ||
      row.centerLng === null ||
      row.radiusKm === null
    ) {
      return null;
    }
    const framing = framingFromSavedAroundMe({
      centerLat: row.centerLat,
      centerLng: row.centerLng,
      radiusKm: row.radiusKm,
    });
    if (framing === null) {
      return null;
    }
    return {
      framingMode: 'radius',
      radiusKm: framing.radiusKm,
      centerLat: framing.centerLat,
      centerLng: framing.centerLng,
      radiusLiveGps: false,
      boundsOverlay: null,
    };
  }
  if (
    row.swLat === null ||
    row.swLng === null ||
    row.neLat === null ||
    row.neLng === null
  ) {
    return null;
  }
  const parsed = parseMapViewBounds({
    swLat: String(row.swLat),
    swLng: String(row.swLng),
    neLat: String(row.neLat),
    neLng: String(row.neLng),
  });
  if (!parsed.ok) {
    return null;
  }
  return {
    framingMode: 'bounds',
    boundsOverlay: normalizeMapViewBounds(parsed.bounds),
    ephemeralRadiusKm: null,
    radiusCenter: null,
    radiusLiveGps: false,
  };
}

/**
 * Approximate WGS84 corners for a circle (meters via radiusKm) — pure, no Leaflet.
 */
export function approxCircleBoundsCorners(
  centerLat: number,
  centerLng: number,
  radiusKm: number,
): readonly [readonly [number, number], readonly [number, number]] {
  const meters = radiusKmToMeters(radiusKm);
  const latDelta = meters / 111_320;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  const lngDenom = 111_320 * Math.max(Math.abs(cosLat), 1e-6);
  const lngDelta = meters / lngDenom;
  return [
    [centerLat - latDelta, centerLng - lngDelta],
    [centerLat + latDelta, centerLng + lngDelta],
  ];
}

const VIEW_NAME_MAX_LEN = 64;

export function parseMapViewName(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > VIEW_NAME_MAX_LEN) {
    return null;
  }
  return trimmed;
}
